@use "../../styles/breakpoint.module.scss" as *;

.form {
    display: block;
    position: relative;
}

.soloForm {
    composes: form;
    max-width: 600px;
    margin: 0 auto;

    .input {
        width: 100%;
    }
}

.complementaryForm {
    composes: form;

    .input {
        max-width: 400px;
    }
}

.fullWidthForm {
    composes: form;
    width: 100%;

    .input,
    .groupedFields {
        max-width: 100%;
    }
}

.threeColumnWrapper {
    grid-column: 3 / span 8;

    @media ($below-desktop) {
        grid-column: 2 / span 10;
    }

    @media ($below-mobile) {
        grid-column: 1 / -1;
    }
}

.threeColumnForm {
    --labui-grid-gap: 0 var(--default-grid-gap);

    composes: form;
    composes: gridContainer from "../../styles/composable/layout.module.css";

    // the rest of the styles are below, this is in here for specificity reasons
    .threeColumnGroup {
        row-gap: 0;
    }

    .input {
        grid-column: span 4;

        @media ($below-tablet) {
            grid-column: span 6;
        }

        @media ($below-mobile) {
            grid-column: 1 / -1;
        }
    }

    .submitContainer {
        margin-top: 0;
    }

    .threeColumnTitle {
        font: var(--font-h-24);
        margin-top: var(--spacer-24);
    }
}

.threeColumnTitle,
.submitContainer {
    composes: threeColumnWrapper;
}

.threeColumnGroup {
    composes: threeColumnWrapper;
    composes: grid from "../../styles/composable/layout.module.css";
}

.loading {
    position: relative;

    > *:not(.loadingUI, .submitContainer) {
        opacity: 0.5;
        cursor: default;
        pointer-events: none;
        user-select: none;
    }
}

.loadingUI {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    font: var(--font-p-14);
    text-align: center;
}

.loader {
    width: var(--spacer-64);
    margin-top: var(--spacer-8);
}

.successUI {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacer-12);
    padding-inline: var(--spacer-24);
    background-color: rgb(var(--white-base) / 90%);
    font: var(--font-p-16);
    text-align: center;
    z-index: 1;

    [data-design-version="v2026"] & {
        background-color: var(--color-primary);
        color: var(--color-contrast);
    }
}

.failureUI {
    composes: successUI;
}

.title {
    font: var(--font-h-24);
}

.input {
    margin-block: var(--spacer-24);

    input,
    textarea {
        width: 100%;
    }
}

.textLink {
    composes: fontTextLink from "../../styles/composable/typography.module.css";

    &:focus {
        outline: var(--outline);
        outline-offset: 1px;
    }
}

.extraField {
    position: absolute;
    inset-inline-start: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
}

.legalWrapper {
    margin-block: var(--spacer-24);
}

.legal {
    font: var(--font-p-14);
}

.submitContainer {
    margin-top: var(--spacer-24);
}

.checkbox {
    composes: outline from "../../styles/composable/utility.module.css";
    display: flex;
    gap: var(--spacer-16);
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    font: var(--font-p-16);
    cursor: pointer;
}

.checkSvg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 3px;
    padding: 3px 2px;
    background-color: transparent;
    box-shadow: inset 0 0 0 2px var(--gray);
    fill: transparent;
    transition:
        box-shadow 0.2s,
        fill 0.2s;

    .checkbox[data-hovered] & {
        box-shadow: inset 0 0 0 2px var(--red);
    }

    .checkbox[data-selected] & {
        box-shadow: inset 0 0 0 16px var(--red);
        fill: var(--white);
    }
}

.code {
    display: flex;
    gap: var(--spacer-16);
    align-items: center;

    .copyClickable {
        --height: 2rem;

        > div {
            padding-inline: 1em;
        }

        .copySvg {
            width: 16px;
            height: 16px;
        }
    }
}

.groupedFields {
    display: flex;
    gap: 0 var(--spacer-16);
    max-width: 400px;

    & > div {
        flex: 1;
        margin: 0;
    }
}

.eventTermsTypography {
    composes: fontCaption from "../../styles/composable/typography.module.css";
    color: var(--dark-brown);
}

.termsWrapper {
    border-bottom: 1px solid var(--dark-brown);
    padding: 0 0 var(--spacer-16) 0;
}

.terms {
    composes: eventTermsTypography;
    background: transparent;
    border: none;
    cursor: pointer;
    margin: 0;
    padding: 0;
    position: relative;
}

.termsArrow {
    position: absolute;
    top: 50%;
    translate: 0 -60%;
    rotate: 90deg;
    width: var(--spacer-16);
    height: var(--spacer-16);
    fill: var(--dark-brown);
}

.termsRichText {
    composes: eventTermsTypography;

    :where(p) {
        font-weight: 700;
        margin-top: var(--spacer-16);
    }
}

.eventLegalText {
    composes: eventTermsTypography;
    color: var(--dark-brown);
}

.productSection {
    margin-block: var(--spacer-24);
}

.productDivider {
    border: none;
    border-top: 1px solid var(--light-gray);
    margin: var(--spacer-32) 0;
}

.productHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacer-24);

    @media ($below-mobile) {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacer-16);
    }
}

.productTitle {
    font: var(--font-h-24);
    margin: 0;
}

.addProductContainer {
    margin-block: var(--spacer-32);
    display: flex;
    justify-content: flex-start;
}
